// src/pages/BigScreenPage.jsx
// The tournament for a big screen at the venue: the current round court by court, big, and the ranking beside it.
// Opens from the share link (/s/<token>/screen), needs no sign-in, updates by itself.
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { formatScore } from '../utils/ranking';
import { TrophyIcon } from '../components/icons';
import { useSharedTournament } from './shared/useSharedTournament';

const RANKING_ROWS = 12;
const nameOf = (player) => `${player.firstName} ${player.lastName}`.trim();
const clock = (date) => date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

function useClock() {
    const [now, setNow] = useState(new Date());
    useEffect(() => {
        const timer = setInterval(() => setNow(new Date()), 15000);
        return () => clearInterval(timer);
    }, []);
    return now;
}

export function playedSets(matchup) {
    return (matchup.matches || []).flatMap((match) => (match.sets || []).filter((set) => set.team1Score + set.team2Score > 0));
}

function Court({ matchup, number }) {
    const sets = playedSets(matchup);
    return (
        <article className={`screen-court${matchup.cancelled ? ' cancelled' : ''}`} aria-label={`Court ${number}`}>
            <h2>Court {number}</h2>
            <div className="screen-teams">
                <div className="screen-team team-a">{(matchup.team1?.players || []).map((player) => <span key={player.playerId}>{nameOf(player)}</span>)}</div>
                <span className="screen-vs">vs</span>
                <div className="screen-team team-b">{(matchup.team2?.players || []).map((player) => <span key={player.playerId}>{nameOf(player)}</span>)}</div>
            </div>
            <p className="screen-score">
                {matchup.cancelled ? 'Cancelled' : sets.length === 0 ? '–' : sets.map((set) => `${set.team1Score}–${set.team2Score}`).join('   ')}
            </p>
        </article>
    );
}

function BigScreenPage() {
    const { token } = useParams();
    const { data, status } = useSharedTournament(token);
    const now = useClock();
    const [fullscreen, setFullscreen] = useState(Boolean(document.fullscreenElement));

    useEffect(() => {
        const onChange = () => setFullscreen(Boolean(document.fullscreenElement));
        document.addEventListener('fullscreenchange', onChange);
        return () => document.removeEventListener('fullscreenchange', onChange);
    }, []);

    const name = data?.tournament?.name;
    useEffect(() => {
        if (name) document.title = `${name} · Tournamental`;
    }, [name]);

    if (status === 'gone') {
        return (
            <main className="big-screen">
                <div className="empty">
                    <TrophyIcon size={48} />
                    <strong>This link is no longer active</strong>
                </div>
            </main>
        );
    }
    if (!data) {
        return (
            <main className="big-screen">
                {status === 'error' ? <p>Could not load the tournament. Trying again…</p> : <div className="skeleton" aria-busy="true" />}
            </main>
        );
    }

    const { tournament, standings, ranking, rounds } = data;
    const latest = [...rounds].sort((a, b) => b.roundId - a.roundId)[0];
    const roundNumber = rounds.length;
    const idle = latest?.idlePlayers || [];

    return (
        <main className="big-screen">
            <header className="screen-head">
                <h1>{tournament.name}</h1>
                <div className="screen-meta">
                    {latest && <span className="screen-round">Round {roundNumber}</span>}
                    <span className={`screen-clock${status === 'stale' ? ' stale' : ''}`}>
                        {status === 'stale' ? 'Connection lost · ' : ''}
                        {clock(now)}
                    </span>
                    {!fullscreen && document.documentElement.requestFullscreen && (
                        <button className="btn btn-sm" onClick={() => document.documentElement.requestFullscreen().catch(() => {})}>
                            Full screen
                        </button>
                    )}
                </div>
            </header>

            <div className="screen-body">
                <section className={`screen-courts${latest && latest.matchups.length > 4 ? ' dense' : ''}`} aria-label="Current round">
                    {!latest ? (
                        <p className="screen-empty">The first round will show up here as soon as it is planned.</p>
                    ) : (
                        latest.matchups.map((matchup, index) => <Court key={matchup.matchupId} matchup={matchup} number={index + 1} />)
                    )}
                    {idle.length > 0 && (
                        <p className="screen-idle">
                            <strong>Sitting out:</strong> {idle.map(nameOf).join(', ')}
                        </p>
                    )}
                </section>

                <section className="screen-ranking" aria-label="Ranking">
                    <h2>Ranking</h2>
                    {standings.length === 0 ? (
                        <p className="screen-empty">No players yet.</p>
                    ) : (
                        <ol>
                            {standings.slice(0, RANKING_ROWS).map((row) => (
                                <li key={row.playerId}>
                                    <span className="rank">{row.rank}</span>
                                    <span className="who">{nameOf(row)}</span>
                                    <span className="points">{formatScore(row.score, ranking)}</span>
                                </li>
                            ))}
                        </ol>
                    )}
                    {standings.length > RANKING_ROWS && <p className="screen-more">and {standings.length - RANKING_ROWS} more</p>}
                </section>
            </div>
        </main>
    );
}

export default BigScreenPage;
